import { Logger, LogTag, ServiceType } from "@lib/monitoring/logger";
import type {
    SearchResultGeocodeLocation,
    SearchResultLocation,
    SearchResultPlaceLocation,
} from "../controller";
export type ParsedAddressComponents = {
    city: string;
    postalCode: string;
    viewport?: google.maps.LatLngBounds;
};

const isGeocoderResult = (result: SearchResultLocation): result is SearchResultGeocodeLocation => {
    return result.type === "geocode";
};

const parseGeocodingResultLocation = (
    result: google.maps.GeocoderResult
): ParsedAddressComponents => {
    const address = result.address_components || [];
    const city =
        address.find((c) => c.types.includes("locality"))?.long_name ||
        address.find((c) => c.types.includes("administrative_area_level_2"))?.long_name ||
        address.find((c) => c.types.includes("administrative_area_level_1"))?.long_name ||
        "";
    const postCode = address.find((c) => c.types.includes("postal_code"))?.long_name || "";
    const viewport = result.geometry?.viewport;

    return {
        city: city ?? "",
        postalCode: postCode ?? "",
        viewport,
    };
};

const isPlaceResult = (result: SearchResultLocation): result is SearchResultPlaceLocation => {
    return result.type === "place";
};

const parsePlaceResultLocation = (result: google.maps.places.Place): ParsedAddressComponents => {
    const address = result.addressComponents || [];
    const city =
        address.find((c) => c.types.includes("locality"))?.longText ||
        address.find((c) => c.types.includes("administrative_area_level_2"))?.longText ||
        address.find((c) => c.types.includes("administrative_area_level_1"))?.longText ||
        "";
    const postCode = address.find((c) => c.types.includes("postal_code"))?.longText || "";
    const viewport = result.viewport ?? undefined;
    return {
        city: city ?? "",
        postalCode: postCode ?? "",
        viewport,
    };
};

export const parseSearchResultLocation = (
    result: SearchResultLocation
): ParsedAddressComponents => {
    if (isGeocoderResult(result)) {
        return parseGeocodingResultLocation(result.object);
    } else if (isPlaceResult(result)) {
        return parsePlaceResultLocation(result.object);
    }
    Logger.error(ServiceType.GOOGLE, "Unknown search result type", {
        type: (result as any).type,
        tag: LogTag.STORES,
    });
    return {
        city: "",
        postalCode: "",
    };
};
